import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SharedMapComponent from "../components/Shared/SharedMapComponent";
import logo from "../../assets/optiven-logo-full.png";
import logo1 from "../assets/og/logo192.png";
import preloaderVideo from "./videos/load.mp4";
import GenericActionButtons from "../components/Shared/GenericActionButtons";
import PlotCounterCard from "../components/PlotCounter";

const ProjectMapPage = ({
  projectId,
  title,
  geojsonData,
  mapConfig = {}, // { center, zoom, minZoom, bounds, imageOverlays, styleConfig, searchPrefix }
  customTypeColorFunction,
  customLegendItems,
  viewProjectLink,
  fillOfferLink,
  expressionOfInterestLink,
}) => {
  const isAuthenticated = Boolean(localStorage.getItem("token"));
  const [plotUnitsData, setPlotUnitsData] = useState([]);
  const [projectData, setProjectData] = useState({
    openPlotsCount: 0,
    reservedPlotsCount: 0,
    soldPlotsCount: 0,
  });
  const [loading, setLoading] = useState(true);

  // Fetch Plot Data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch("https://workspace.optiven.co.ke/api/plots");
        if (response.ok) {
          const data = await response.json();
          setTimeout(() => {
            setPlotUnitsData(data);
            setLoading(false);
            window.scrollTo(0, 0);
          }, 500);
        } else {
          console.error("Failed to fetch plot data");
        }
      } catch (error) {
        console.error("Error fetching plot data:", error);
      }
    };

    fetchData();
  }, []);

  // Fetch Project Specific Data (for counts/stats if needed, though SharedMapComponent calculates from plotUnitsData mostly, 
  // but existing code fetches this for "open", "reserved", "sold" stats passed to Legend. 
  // Our SharedMapComponent calculates colors dynamically, but if we want to show a summary Legend with counts, we might need this.
  // The SharedMapComponent's legend is static "Open/Reserved/Sold" labels. 
  // The existing "Legend" component accepted "open", "reserved", "sold" counts. 
  // My SharedMapComponent legend is static labels. 
  // If the user wants counts in the legend, I should update SharedMapComponent to accept them or calculate them.
  // The existing `Legend` component in `AchieversPark` (imported from `../components/ACHIEVERS/Legend`) takes `open`, `reserved`, `sold`.
  // Let's implement fetching this data anyway, just in case we enhance the legend later.
  useEffect(() => {
    if (!projectId) return;

    const fetchProjectData = async () => {
      try {
        const response = await fetch(
          `https://workspace.optiven.co.ke/api/plots/all/${projectId}`
        );
        if (response.ok) {
          const data = await response.json();
          setProjectData(data);
        } else {
          console.error("Failed to fetch project data");
        }
      } catch (error) {
        console.error("Error fetching project data:", error);
      }
    };

    fetchProjectData();
  }, [projectId]);

  const styleConfigWithDefaults = mapConfig.styleConfig || { mode: "plot_status", sizeField: "plot_size" };

  return (
    <>
      <div className="navbar bg-primary">
        <Link className="btn btn-ghost normal-case text-xl" to="/map-home">
          <img src={logo} alt="logo" className="w-40" />
        </Link>
      </div>
      <div className="min-h-screen flex flex-col">
        <div className="header text-center my-4">
          <h1 className="text-3xl font-bold text-[#006000]">
            <b>{title}</b>
          </h1>
        </div>

        {loading ? (
          <div
            className="loading-container relative flex flex-col justify-center items-center h-[90vh]"
          >
            <video
              autoPlay
              loop
              muted
              className="absolute w-full h-full object-cover -z-10"
            >
              <source src={preloaderVideo} type="video/mp4" />
            </video>
            <img
              src={logo1}
              alt="Your Logo"
              style={{ width: "100px", height: "120px" }}
            />
            <div className="text-center">
              <div role="status">
                <svg
                  aria-hidden="true"
                  className="inline w-8 h-8 mr-2 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
                  viewBox="0 0 100 101"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                    fill="currentColor"
                  />
                  <path
                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                    fill="currentFill"
                  />
                </svg>
                <span className="sr-only">Loading...</span>
              </div>
            </div>
            <div className="text-center mt-4 text-slate-700">
               <p style={{ color: "brown", fontWeight: "bold" }}>
                Please wait as the map is being optimized...
              </p>
            </div>
          </div>
        ) : (
          <div>
            <SharedMapComponent
              geojsonData={geojsonData}
              plotUnitsData={plotUnitsData}
              center={mapConfig.center}
              zoom={mapConfig.zoom}
              minZoom={mapConfig.minZoom}
              bounds={mapConfig.bounds}
              imageOverlays={mapConfig.imageOverlays}
              styleConfig={styleConfigWithDefaults}
              customTypeColorFunction={customTypeColorFunction}
              customLegendItems={customLegendItems}
              searchPrefix={mapConfig.searchPrefix}
              canViewAvailability={isAuthenticated}
            />
             {/* Legend and Actions Area */}
            <div
              className={`grid grid-cols-1 ${
                isAuthenticated ? "md:grid-cols-2 lg:grid-cols-2" : ""
              } gap-10 m-10`}
            >
              {isAuthenticated && (
                <PlotCounterCard
                  openPlotsNumber={projectData.openPlotsCount}
                  reservedPlotsNumber={projectData.reservedPlotsCount}
                  soldPlotsNumber={projectData.soldPlotsCount}
                />
              )}
              <GenericActionButtons
                viewProjectLink={viewProjectLink}
                fillOfferLink={fillOfferLink}
                expressionOfInterestLink={expressionOfInterestLink}
                showRestrictedActions={isAuthenticated}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ProjectMapPage;
